/**
 * Platform marks for the download surfaces.
 *
 * Inline SVG rather than an icon font or image assets: these render at
 * button size inside MUI `sx` trees, need to inherit `currentColor`
 * (except Home Assistant, which has a mandated brand colour), and must
 * not cost a network round-trip on the landing page's first paint.
 *
 * Paths are the vendors' own logo geometry (Simple Icons, CC0). Apple,
 * Microsoft, and the HA Foundation each have trademark guidelines about
 * their marks: we use them only to identify which build a button
 * downloads — a nominative use — never as decoration or endorsement.
 */
import React from 'react';
import {Box} from '@mui/material';

type IconProps = {
  size?: number;
  /** Overrides `currentColor` inheritance. */
  color?: string;
};

function Svg({
  children,
  size = 20,
  color,
  viewBox = '0 0 24 24',
  title,
}: IconProps & {
  children: React.ReactNode;
  viewBox?: string;
  title: string;
}): React.JSX.Element {
  return (
    <Box
      component="svg"
      role="img"
      aria-label={title}
      viewBox={viewBox}
      sx={{
        width: size,
        height: size,
        display: 'block',
        flexShrink: 0,
        color: color ?? 'inherit',
        fill: 'currentColor',
      }}
    >
      {children}
    </Box>
  );
}

export function AppleIcon(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props} title="macOS">
      <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
    </Svg>
  );
}

export function WindowsIcon(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props} title="Windows">
      <path d="M0 3.449L9.75 2.1v9.451H0m10.949-9.602L24 0v11.4H10.949M0 12.6h9.75v9.451L0 20.699M10.949 12.6H24V24l-12.9-1.801" />
    </Svg>
  );
}

export function LinuxIcon(props: IconProps): React.JSX.Element {
  return (
    <Svg {...props} title="Linux">
      <path d="M12.504 0c-.155 0-.315.008-.48.021-4.226.333-3.105 4.807-3.17 6.298-.076 1.092-.3 1.953-1.05 3.02-.885 1.051-2.127 2.75-2.716 4.521-.278.832-.41 1.684-.287 2.489a.424.424 0 00-.11.135c-.26.268-.45.6-.663.839-.199.199-.485.267-.797.4-.313.136-.658.269-.864.68-.09.189-.136.394-.132.602 0 .199.027.4.055.6.058.399.116.728.04.978-.249.68-.28 1.145-.106 1.484.174.334.535.47.94.601.81.2 1.91.135 2.774.6.926.466 1.866.67 2.616.47.526-.116.97-.464 1.208-.946.587-.003 1.23-.269 2.26-.334.699-.058 1.574.267 2.577.2.025.134.063.198.114.333l.003.003c.391.778 1.113 1.132 1.884 1.071.771-.06 1.592-.536 2.257-1.306.631-.765 1.683-1.084 2.378-1.503.348-.199.629-.469.649-.853.023-.4-.2-.811-.714-1.376v-.097l-.003-.003c-.17-.2-.25-.535-.338-.926-.085-.401-.182-.786-.492-1.046h-.003c-.059-.054-.123-.067-.188-.135a.357.357 0 00-.19-.064c.431-1.278.264-2.55-.173-3.694-.533-1.41-1.465-2.638-2.175-3.483-.796-1.005-1.576-1.957-1.56-3.368.026-2.152.236-6.133-3.544-6.139zm.529 3.405h.013c.213 0 .396.062.584.198.19.135.33.332.438.533.105.259.158.459.166.724 0-.02.006-.04.006-.06v.105a.086.086 0 01-.004-.021l-.004-.024a1.807 1.807 0 01-.15.706.953.953 0 01-.213.335.71.71 0 00-.088-.042c-.104-.045-.198-.064-.284-.133a1.312 1.312 0 00-.22-.066c.05-.06.146-.133.183-.198.053-.128.082-.264.088-.402v-.02a1.21 1.21 0 00-.061-.4c-.045-.134-.101-.2-.183-.333-.084-.066-.167-.132-.267-.132h-.016c-.093 0-.176.03-.262.132a.8.8 0 00-.205.334 1.18 1.18 0 00-.09.4v.019c.002.089.008.179.02.267-.193-.067-.438-.135-.607-.202a1.635 1.635 0 01-.018-.2v-.02a1.772 1.772 0 01.15-.768c.082-.22.232-.406.43-.533a.985.985 0 01.594-.2zm-2.962.059h.036c.142 0 .27.048.399.135.146.129.264.288.344.465.09.199.14.4.153.667v.004c.007.134.006.2-.002.266v.08c-.03.007-.056.018-.083.024-.152.055-.274.135-.393.2.012-.09.013-.18.003-.267v-.015c-.012-.133-.04-.2-.082-.333a.613.613 0 00-.166-.267.248.248 0 00-.183-.064h-.021c-.071.006-.13.041-.186.132a.552.552 0 00-.12.27.944.944 0 00-.023.33v.015c.012.135.037.2.08.334.046.134.098.2.166.268.01.009.02.018.034.024-.07.057-.117.07-.176.136a.304.304 0 01-.131.068 2.62 2.62 0 01-.275-.402 1.772 1.772 0 01-.155-.667 1.759 1.759 0 01.08-.668 1.43 1.43 0 01.283-.535c.128-.133.26-.2.418-.2zm1.37 1.706c.332 0 .733.065 1.216.399.293.2.523.269 1.052.468h.003c.255.136.405.266.478.399v-.131a.571.571 0 01.016.47c-.123.31-.516.643-1.063.842v.002c-.268.135-.501.333-.775.465-.276.135-.588.292-1.012.267a1.139 1.139 0 01-.448-.067 3.566 3.566 0 01-.322-.198c-.195-.135-.363-.332-.612-.465v-.005h-.005c-.4-.246-.616-.512-.686-.71-.07-.268-.005-.47.193-.6.224-.135.38-.271.483-.336.104-.074.143-.102.176-.131h.002v-.003c.169-.202.436-.47.839-.601.139-.036.294-.065.466-.065zm2.8 2.142c.358 1.417 1.196 3.475 1.735 4.473.286.534.855 1.659 1.102 3.024.156-.005.33.018.513.064.646-1.671-.546-3.467-1.089-3.966-.22-.2-.232-.335-.123-.335.59.534 1.365 1.572 1.646 2.757.13.535.16 1.104.021 1.67.067.028.135.06.205.067 1.032.534 1.413.938 1.23 1.537v-.043c-.06-.003-.12 0-.18 0h-.016c.151-.467-.182-.825-1.065-1.224-.915-.4-1.646-.336-1.77.465-.008.043-.013.066-.018.135-.068.023-.139.053-.209.064-.43.268-.662.669-.793 1.187-.13.533-.17 1.156-.205 1.869v.003c-.02.334-.17.838-.319 1.35-1.5 1.072-3.58 1.538-5.348.334a2.645 2.645 0 00-.402-.533 1.45 1.45 0 00-.275-.333c.182 0 .338-.03.465-.067a.615.615 0 00.314-.334c.108-.267 0-.697-.345-1.163-.345-.467-.931-.995-1.788-1.521-.63-.4-.986-.87-1.15-1.396-.165-.534-.143-1.085-.015-1.645.245-1.07.873-2.11 1.274-2.763.107-.065.037.135-.408.974-.396.751-1.14 2.497-.122 3.854a8.123 8.123 0 01.647-2.876c.564-1.278 1.743-3.504 1.836-5.268.048.036.217.135.289.202.218.133.38.333.59.465.21.201.477.335.876.335.039.003.075.006.11.006.412 0 .73-.134.983-.268.278-.135.505-.334.73-.4h.005c.478-.135.85-.4 1.057-.7z" />
    </Svg>
  );
}

/**
 * Home Assistant's house-and-circuit mark. Rendered in the official
 * brand blue (#18BCF2) unless a colour is passed — the mark is only
 * recognisable to HA users in its own colour.
 */
export function HomeAssistantIcon({
  size = 20,
  color = '#18BCF2',
}: IconProps): React.JSX.Element {
  return (
    <Svg size={size} color={color} title="Home Assistant">
      <path d="M23.5854 11.0129 13.077.5045a1.5177 1.5177 0 0 0-2.1471 0L6.4907 4.951v-.6482a.7161.7161 0 0 0-.716-.716h-.6879a.7161.7161 0 0 0-.716.716v3.0473L.4146 11.013a1.4132 1.4132 0 0 0-.4146 1v10.5723a1.4132 1.4132 0 0 0 1.4133 1.4133h8.4271l-4.3891-4.389a1.6242 1.6242 0 0 1-.5502.0975 1.6062 1.6062 0 1 1 1.6062-1.6062 1.6242 1.6242 0 0 1-.0975.5502l3.2812 3.2812V9.9518a1.6062 1.6062 0 1 1 1.4133 0v3.8399l3.2624-3.2624a1.5928 1.5928 0 0 1-.0975-.5502 1.6062 1.6062 0 1 1 1.6062 1.6062 1.5928 1.5928 0 0 1-.5502-.0975l-4.2209 4.2209v8.2911h11.7207A1.4133 1.4133 0 0 0 24 22.5854V12.0131a1.4132 1.4132 0 0 0-.4146-1.0002z" />
    </Svg>
  );
}
